import type { DailyTotals, Meal } from './api';
import { filteredIngredients } from './meals';

// Plain-text rendering of one day's eating plan, for the Share action on
// Today's "Meals" card and on "Prep ahead". The output lands in SMS, Notes or
// a chat app, so: no markdown, no emoji, no thousands separators, and every
// line either carries real data or is left out entirely. Nothing here renders
// — it is a pure function so the exact bytes can be asserted in a test.

export type MealPlanTextInput = {
  // YYYY-MM-DD. Anything unparseable degrades to "no weekday" rather than
  // printing "Invalid Date".
  dateISO: string;
  block?: string | null;
  weekNumber?: number | null;
  meals: Meal[];
  // The template's day target. Preferred over summing the meals because it is
  // the only source that carries carbs.
  dailyTotals?: DailyTotals | null;
  eatingWindow?: { start: string; end: string } | null;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Template labels carry their own scheduled time in parentheses
// ("Meal 1 · break the fast (11:00)"). The time is printed on the same line
// from `scheduled_time`, so strip it rather than say 11:00 twice.
const TRAILING_TIME_RE = /\s*\(\s*\d{1,2}:\d{2}\s*\)\s*$/;

export function formatMealPlanText(input: MealPlanTextInput): string {
  const blocks: string[] = [];

  const header = [headerLine(input.dateISO), contextLine(input.block, input.weekNumber)]
    .filter((l): l is string => l !== null)
    .join('\n');
  if (header) blocks.push(header);

  for (const meal of input.meals) {
    const block = mealBlock(meal);
    if (block) blocks.push(block);
  }

  const footer = [totalLine(input.meals, input.dailyTotals), windowLine(input.eatingWindow)]
    .filter((l): l is string => l !== null)
    .join('\n');
  if (footer) blocks.push(footer);

  return blocks.join('\n\n');
}

function headerLine(dateISO: string): string {
  const date = (dateISO ?? '').trim();
  const stamp = [weekdayShort(date), date].filter(Boolean).join(' ');
  return stamp ? `GIOS PROTOCOL · ${stamp}` : 'GIOS PROTOCOL';
}

// weekdayShort reads the day off a YYYY-MM-DD string at UTC noon, so it can't
// slip a day the way a local-midnight Date does in UTC+ timezones.
function weekdayShort(dateISO: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return '';
  const ms = Date.parse(`${dateISO}T12:00:00Z`);
  if (Number.isNaN(ms)) return '';
  return WEEKDAYS[new Date(ms).getUTCDay()];
}

function contextLine(block: string | null | undefined, week: number | null | undefined): string | null {
  const parts: string[] = [];
  if (block) parts.push(`Block ${block}`);
  if (typeof week === 'number' && Number.isFinite(week) && week > 0) parts.push(`Week ${week}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function mealBlock(meal: Meal): string | null {
  const lines: string[] = [];

  const heading = [mealHeading(meal.label ?? ''), (meal.scheduled_time ?? '').trim()]
    .filter(Boolean)
    .join(' · ');
  if (heading) lines.push(heading);

  const macros = macroLine(meal.calories, meal.protein_g, meal.fat_g, undefined);
  if (macros) lines.push(macros);

  const ingredients = meal.ingredients ? filteredIngredients(meal) : [];
  for (const ing of ingredients) {
    // Amount and item stay on one line: "5 large whole eggs".
    const text = `${(ing.amount ?? '').trim()} ${(ing.item ?? '').trim()}`.trim();
    if (text) lines.push(`- ${text}`);
  }

  return lines.length > 0 ? lines.join('\n') : null;
}

// mealHeading uppercases only the slot ("Meal 1" → "MEAL 1") and leaves the
// descriptive half alone, so a shouted line doesn't swallow the whole label.
function mealHeading(label: string): string {
  const clean = label.replace(TRAILING_TIME_RE, '').trim();
  if (!clean) return '';
  const sep = clean.indexOf('·');
  if (sep < 0) return clean.toUpperCase();
  const slot = clean.slice(0, sep).trim();
  const rest = clean.slice(sep + 1).trim();
  if (!slot) return rest;
  if (!rest) return slot.toUpperCase();
  return `${slot.toUpperCase()} · ${rest}`;
}

// "1700 kcal · 170P / 100F / 40C", with any component the data doesn't carry
// simply absent. Returns null when nothing at all is known.
function macroLine(
  kcal: number | null | undefined,
  protein: number | null | undefined,
  fat: number | null | undefined,
  carbs: number | null | undefined,
): string | null {
  const segments: string[] = [];
  if (isPositive(kcal)) segments.push(`${Math.round(kcal)} kcal`);

  const macros: string[] = [];
  if (isCount(protein)) macros.push(`${Math.round(protein)}P`);
  if (isCount(fat)) macros.push(`${Math.round(fat)}F`);
  if (isCount(carbs)) macros.push(`${Math.round(carbs)}C`);
  if (macros.length > 0) segments.push(macros.join(' / '));

  return segments.length > 0 ? segments.join(' · ') : null;
}

// The day target comes from `daily_totals` when the template declares one —
// it is the same number "Day target" shows and the only source that knows
// carbs. Otherwise the totals are added up from the meals printed above, and
// a macro is only summed when every meal declares it, so a partial plan omits
// the component instead of quietly understating it.
function totalLine(meals: Meal[], dailyTotals: DailyTotals | null | undefined): string | null {
  if (dailyTotals) {
    const line = macroLine(
      dailyTotals.calories,
      dailyTotals.protein_g,
      dailyTotals.fat_g,
      dailyTotals.carbs_g,
    );
    return line ? `TOTAL ${line}` : null;
  }
  if (meals.length === 0) return null;
  const line = macroLine(
    sumIfAll(meals, (m) => m.calories),
    sumIfAll(meals, (m) => m.protein_g),
    sumIfAll(meals, (m) => m.fat_g),
    undefined,
  );
  return line ? `TOTAL ${line}` : null;
}

function sumIfAll(meals: Meal[], pick: (m: Meal) => number | null | undefined): number | undefined {
  let total = 0;
  for (const m of meals) {
    const v = pick(m);
    if (!isCount(v)) return undefined;
    total += v;
  }
  return total;
}

function windowLine(w: { start: string; end: string } | null | undefined): string | null {
  const start = (w?.start ?? '').trim();
  const end = (w?.end ?? '').trim();
  if (!start || !end) return null;
  return `Eating window ${start}–${end}`;
}

function isPositive(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0;
}

function isCount(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0;
}
